"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import {
  Pencil,
  Eraser,
  RotateCcw,
  Loader2,
  Send,
  Sparkles,
  X,
} from "lucide-react";
import { EditCanvas, EditCanvasHandle } from "./EditCanvas";
import { useServiceStatus } from "@/hooks/useServiceStatus";

export interface EditVersion {
  imageUrl: string;
  prompt: string;
  timestamp: number;
}

/** Diff + plant-aware side effects of an edit (PLA2-548/549/550/551/556).
 *  Only populated when the caller opts into reconciliation (see
 *  `projectId`/`parentGenerationId`/`countPlants` below) AND the backend's
 *  post-edit identification pass succeeded. */
export interface PlantReconciliationResult {
  plantCounts?: Record<string, number>;
  plantChanges?: {
    added: string[];
    removed: string[];
    swapped: { removed: string; added: string }[];
  };
  diagramUrl?: string;
}

interface EditPanelProps {
  imageUrl: string;
  season: string;
  initialHistory?: EditVersion[];
  onEditComplete: (newImageUrl: string) => void;
  onVersionSelect: (imageUrl: string) => void;
  onHistoryChange?: (history: EditVersion[]) => void;
  onClose: () => void;
  /** When provided, uses this external canvas handle instead of the built-in EditCanvas.
   *  The embedded canvas section is hidden — drawing tools are on the external canvas. */
  externalCanvasRef?: React.RefObject<EditCanvasHandle | null>;
  /** Override label shown in the header chip (e.g. "v2", "Original").
   *  When provided, EditPanel won't compute its own label. */
  versionLabel?: string;
  /** When provided and it resolves to a data URL, that image (e.g. the design
   *  with annotation marks baked in) is sent to the edit endpoint instead of
   *  the plain current image. Resolves to null to fall back to default. */
  getImageOverride?: () => Promise<string | null>;
  /** Whether an image override is currently available (e.g. unsaved marks exist).
   *  Lets the user apply an edit using only the marks, with no typed prompt. */
  hasImageOverride?: boolean;
  /** PLA2-548: the DB-linked project this design belongs to. Required
   *  (together with `parentGenerationId`) to opt into the plant-aware
   *  reconciliation edit path — otherwise the lightweight, DB-free edit is
   *  used exactly as before. */
  projectId?: string;
  /** PLA2-548: the Generation row currently being edited. See `projectId`. */
  parentGenerationId?: string;
  /** Genus alias ids the edit is explicitly introducing (e.g. "add these
   *  plants"), so the post-edit identification pass knows the full
   *  original ∪ added candidate set. */
  additionalGenusAliasIds?: string[];
  /** Run the post-edit plant-identification + quantity pass. Only takes
   *  effect when `projectId`/`parentGenerationId` are also set. */
  countPlants?: boolean;
  /** Re-run the planting diagram against the edited image. Takes effect on
   *  EITHER edit path — the DB-backed `/edit` path (with `projectId`/
   *  `parentGenerationId`) or the live consumer-edit path below. */
  generateDiagram?: boolean;
  /** PLA2-549/550/551/556: LIVE plant reconciliation on the edit endpoint
   *  the frontend actually uses (`/edit-consumer-image` — no designer2
   *  Generation id needed). Only takes effect when `projectId`/
   *  `parentGenerationId` are NOT set (otherwise the DB-backed `/edit`
   *  path above is used instead). The backend only reconciles when the
   *  edited `season` is "Summer" — pass it regardless, it's a no-op on
   *  other seasons (avoids false "removed" plants from winter/autumn
   *  dormancy). */
  reconcilePlants?: boolean;
  /** The design's current selected plant ids, plus any newly added in this
   *  edit — the candidate set the post-edit identification pass checks. */
  candidatePlantIds?: string[];
  /** id -> quantity BEFORE this edit (current shopping-list counts). Lets
   *  the backend tell "still there" apart from "newly added". */
  priorCounts?: Record<string, number>;
  /** Fired when the edit response carries plantCounts/plantChanges/diagramUrl
   *  — the caller should update its shopping list / diagram / cart state. */
  onPlantChanges?: (result: PlantReconciliationResult) => void;
}

const SUGGESTION_PROMPTS = [
  "Make the plants fuller and more lush",
  "Add a stone retaining wall",
  "Add decorative garden edging",
  "Make the colors more vibrant",
  "Add ground cover between plants",
  "Add landscape lighting",
  "Add a garden path",
  "Remove the plant on the left",
];

export function EditPanel({
  imageUrl,
  season,
  initialHistory,
  onEditComplete,
  onVersionSelect,
  onHistoryChange,
  onClose,
  externalCanvasRef,
  versionLabel: externalVersionLabel,
  getImageOverride,
  hasImageOverride,
  projectId,
  parentGenerationId,
  additionalGenusAliasIds,
  countPlants,
  generateDiagram,
  reconcilePlants,
  candidatePlantIds,
  priorCounts,
  onPlantChanges,
}: EditPanelProps) {
  const internalCanvasRef = useRef<EditCanvasHandle>(null);
  const canvasRef = externalCanvasRef || internalCanvasRef;
  const useExternalCanvas = !!externalCanvasRef;
  const [prompt, setPrompt] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { unavailable: serviceUnavailable } = useServiceStatus();
  const [isEraser, setIsEraser] = useState(false);
  const [brushSize, setBrushSize] = useState(5);
  const [currentImageUrl, setCurrentImageUrl] = useState(
    initialHistory && initialHistory.length > 0
      ? initialHistory[initialHistory.length - 1].imageUrl
      : imageUrl
  );
  const [editHistory, setEditHistory] = useState<EditVersion[]>(
    initialHistory && initialHistory.length > 0
      ? initialHistory
      : [{ imageUrl, prompt: "Original", timestamp: Date.now() }]
  );
  const [showSuggestions, setShowSuggestions] = useState(true);

  // Sync currentImageUrl when parent changes imageUrl (e.g. version selected from timeline)
  useEffect(() => {
    if (imageUrl && imageUrl !== currentImageUrl) {
      setCurrentImageUrl(imageUrl);
    }
  }, [imageUrl]);

  // Sync editHistory when parent updates initialHistory (e.g. season change)
  useEffect(() => {
    if (initialHistory && initialHistory.length > 0) {
      setEditHistory(initialHistory);
    }
  }, [initialHistory]);

  // Which version is currently selected (for the chip label)
  const selectedVersionIndex = editHistory.findIndex(
    (v) => v.imageUrl === currentImageUrl
  );
  const computedLabel =
    selectedVersionIndex === 0
      ? "Original"
      : selectedVersionIndex > 0
        ? `v${selectedVersionIndex + 1}`
        : `v${editHistory.length}`;
  const selectedVersionLabel = externalVersionLabel ?? computedLabel;

  const handleVersionSelect = (version: EditVersion) => {
    setCurrentImageUrl(version.imageUrl);
    onVersionSelect(version.imageUrl);
  };
  const [feedbackMessage, setFeedbackMessage] = useState<{
    type: "success" | "error" | "warning";
    text: string;
  } | null>(null);

  const showFeedback = (
    type: "success" | "error" | "warning",
    text: string
  ) => {
    setFeedbackMessage({ type, text });
    if (type !== "error") {
      setTimeout(() => setFeedbackMessage(null), 4000);
    }
  };

  const handleSubmit = useCallback(async () => {
    // PLA2-945: the edit endpoint lives on the design backend.
    if (serviceUnavailable) {
      showFeedback(
        "warning",
        "The designer is temporarily unavailable. Please try again shortly."
      );
      return;
    }
    if (!prompt.trim() && !canvasRef.current?.hasMask() && !hasImageOverride) {
      showFeedback(
        "warning",
        "Please describe what you'd like to change, or draw on the image to mark an area."
      );
      return;
    }

    setIsSubmitting(true);
    setFeedbackMessage(null);

    try {
      const hasMask = canvasRef.current?.hasMask() ?? false;

      const editPrompt =
        prompt.trim() ||
        "Modify the marked area to blend naturally with the surrounding landscape.";

      // Annotation marks (circles/arrows/scribbles) take priority: send the
      // design with those marks flattened in so the AI can see them. Falls back
      // to the edit-canvas mask composite, then the plain current image.
      const overrideImage = getImageOverride ? await getImageOverride() : null;
      let compositeData: string | null = null;
      if (!overrideImage && hasMask) {
        compositeData = canvasRef.current?.getCompositeDataUrl() ?? null;
      }
      const imageToSend = overrideImage || compositeData || currentImageUrl;
      const hasDrawings = hasMask || !!overrideImage;

      const response = await fetch("/api/designs/edit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          editedImageUrl: imageToSend,
          prompt: editPrompt,
          season,
          hasDrawings,
          // PLA2-548: opt into the DB-backed plant-aware reconciliation edit
          // path. Both must be present or the proxy falls back to the
          // consumer edit below.
          projectId,
          parentGenerationId,
          additionalGenusAliasIds,
          countPlants,
          generateDiagram,
          // PLA2-549/550/551/556: LIVE plant reconciliation on the consumer
          // edit path (used whenever projectId/parentGenerationId are unset).
          reconcilePlants,
          candidatePlantIds,
          priorCounts,
        }),
      });

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ detail: "Edit failed" }));
        throw new Error(error.detail || "Edit failed");
      }

      const result = await response.json();
      // Strip presigned params so the URL survives session restore
      // (bucket has public-read policy, permanent URLs work fine)
      const { toPermanentUrl } = await import("@/lib/utils");
      const newImageUrl = toPermanentUrl(result.editedImageUrl);

      const newVersion: EditVersion = {
        imageUrl: newImageUrl,
        prompt: editPrompt,
        timestamp: Date.now(),
      };

      const newHistory = [...editHistory, newVersion];
      setEditHistory(newHistory);
      setCurrentImageUrl(newImageUrl);
      setPrompt("");
      canvasRef.current?.clearMask();
      onEditComplete(newImageUrl);
      onHistoryChange?.(newHistory);

      // PLA2-548/549/550/551/556: surface plant reconciliation results, if
      // the backend ran the post-edit identification pass (countPlants /
      // generateDiagram opted in above, and the pass succeeded — it's
      // non-fatal server-side, so this may simply be absent).
      if (result.plantChanges || result.plantCounts || result.diagramUrl) {
        onPlantChanges?.({
          plantCounts: result.plantCounts,
          plantChanges: result.plantChanges,
          diagramUrl: result.diagramUrl,
        });

        const added: string[] = result.plantChanges?.added ?? [];
        const removed: string[] = result.plantChanges?.removed ?? [];
        if (added.length || removed.length) {
          const parts: string[] = [];
          if (removed.length) parts.push(`Removed: ${removed.join(", ")}`);
          if (added.length) parts.push(`Added: ${added.join(", ")}`);
          toast.info(parts.join(" · "), { autoClose: 6000 });
        }
      }

      showFeedback("success", "Edit applied successfully!");
    } catch (error) {
      showFeedback(
        "error",
        error instanceof Error ? error.message : "Failed to apply edit"
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    serviceUnavailable,
    prompt,
    currentImageUrl,
    season,
    onEditComplete,
    getImageOverride,
    hasImageOverride,
    projectId,
    parentGenerationId,
    additionalGenusAliasIds,
    countPlants,
    generateDiagram,
    reconcilePlants,
    candidatePlantIds,
    priorCounts,
    onPlantChanges,
  ]);

  // Shared brush/eraser/size toolbar — rendered in the canvas section above
  // the prompt (GC flow only; the Pro external-canvas flow has its own tools).
  const renderToolbar = () => (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="flex gap-1 bg-gray-100 dark:bg-plantista-800 rounded-lg p-1">
        <button
          onClick={() => setIsEraser(false)}
          className={`p-2 rounded-md transition ${
            !isEraser
              ? "bg-plantista-500 dark:bg-plantista-400 text-white dark:text-plantista-950 shadow"
              : "text-gray-600 dark:text-plantista-300 hover:bg-gray-200 dark:hover:bg-plantista-700"
          }`}
          title="Brush"
        >
          <Pencil className="w-4 h-4" />
        </button>
        <button
          onClick={() => setIsEraser(true)}
          className={`p-2 rounded-md transition ${
            isEraser
              ? "bg-plantista-500 dark:bg-plantista-400 text-white dark:text-plantista-950 shadow"
              : "text-gray-600 dark:text-plantista-300 hover:bg-gray-200 dark:hover:bg-plantista-700"
          }`}
          title="Eraser"
        >
          <Eraser className="w-4 h-4" />
        </button>
      </div>

      <button
        onClick={() => canvasRef.current?.clearMask()}
        className="p-2 rounded-lg bg-gray-100 dark:bg-plantista-800 text-gray-600 dark:text-plantista-300 hover:bg-gray-200 dark:hover:bg-plantista-700 transition"
        title="Clear mask"
      >
        <RotateCcw className="w-4 h-4" />
      </button>

      <div className="flex items-center gap-2 ml-2">
        <span className="text-xs text-plantista-500 dark:text-plantista-400 font-medium">
          Size
        </span>
        <input
          type="range"
          min="1"
          max="15"
          value={brushSize}
          onChange={(e) => setBrushSize(Number(e.target.value))}
          className="w-20 accent-plantista-500 dark:accent-plantista-400"
        />
        <span className="text-xs text-plantista-600 dark:text-plantista-300 bg-plantista-100 dark:bg-plantista-800 px-1.5 py-0.5 rounded-full font-mono">
          {brushSize}
        </span>
      </div>
    </div>
  );

  return (
    <div className="bg-white dark:bg-plantista-900 border-2 border-gray-200 dark:border-plantista-800 overflow-hidden shadow-lg animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-plantista-800 bg-gray-50 dark:bg-plantista-950">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-plantista-500 dark:text-plantista-400" />
          <h3 className="text-sm font-bold text-plantista-950 dark:text-white">
            Edit {season} Design
          </h3>
          {selectedVersionLabel && (
            <span
              className={`text-xs px-2 py-0.5 rounded-full ${
                selectedVersionLabel === "Original"
                  ? "text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30"
                  : "text-plantista-500 dark:text-plantista-400 bg-plantista-100 dark:bg-plantista-800"
              }`}
            >
              {selectedVersionLabel}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-plantista-800 text-gray-500 dark:text-plantista-400 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Toolbar + hero-sized drawable canvas — the whole edit surface lives in
          this one panel (toolbar on top, image in the middle, prompt below).
          Hidden when using an external canvas (Pro editor owns its own). */}
      {!useExternalCanvas && (
        <>
          {/* Toolbar section */}
          <div className="border-b border-gray-200 dark:border-plantista-800">
            <div className="flex items-center gap-2 px-5 pt-3 pb-1 text-sm font-medium text-plantista-700 dark:text-plantista-300">
              <Pencil className="w-4 h-4" />
              Draw on the image to mark an area (optional)
            </div>
            <div className="px-5 pb-3">{renderToolbar()}</div>
          </div>

          {/* Canvas — hero-sized, flush with the panel edges */}
          <EditCanvas
            ref={internalCanvasRef}
            imageUrl={currentImageUrl}
            brushSize={brushSize}
            isEraser={isEraser}
            className="w-full aspect-video md:aspect-[16/9] border-b border-gray-200 dark:border-plantista-800"
          />
        </>
      )}

      {/* Prompt Area */}
      <div className="px-5 py-4 space-y-3">
        {/* Suggestions */}
        {showSuggestions && !prompt && (
          <div className="flex flex-wrap gap-2">
            {SUGGESTION_PROMPTS.slice(0, 4).map((suggestion) => (
              <button
                key={suggestion}
                onClick={() => {
                  setPrompt(suggestion);
                  setShowSuggestions(false);
                }}
                className="text-xs px-3 py-1.5 rounded-full border border-gray-200 dark:border-plantista-700 text-plantista-700 dark:text-plantista-300 hover:bg-plantista-50 dark:hover:bg-plantista-800 hover:border-plantista-300 dark:hover:border-plantista-600 transition-colors"
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}

        {/* Input + Submit */}
        <div className="flex gap-2">
          <textarea
            value={prompt}
            onChange={(e) => {
              setPrompt(e.target.value);
              if (showSuggestions) setShowSuggestions(false);
            }}
            onFocus={() => setShowSuggestions(false)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            placeholder="Describe what you'd like to change..."
            disabled={isSubmitting || serviceUnavailable}
            rows={2}
            className="flex-1 resize-none border-2 border-gray-200 dark:border-plantista-700 bg-white dark:bg-plantista-950 px-4 py-3 text-sm text-plantista-950 dark:text-white placeholder:text-gray-400 dark:placeholder:text-plantista-500 focus:border-plantista-500 dark:focus:border-plantista-400 focus:outline-none transition-colors disabled:opacity-50"
          />
          <button
            onClick={handleSubmit}
            disabled={
              isSubmitting ||
              serviceUnavailable ||
              (!prompt.trim() &&
                !canvasRef.current?.hasMask() &&
                !hasImageOverride)
            }
            className="self-end px-4 py-3 bg-plantista-500 dark:bg-plantista-400 text-white dark:text-plantista-950 hover:bg-plantista-600 dark:hover:bg-plantista-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm flex items-center gap-2 shadow-md"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="hidden sm:inline">Editing...</span>
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                <span className="hidden sm:inline">Apply</span>
              </>
            )}
          </button>
        </div>

        {/* Feedback message */}
        {feedbackMessage && (
          <div
            className={`flex items-center gap-2 p-3 rounded-xl text-sm ${
              feedbackMessage.type === "success"
                ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300"
                : feedbackMessage.type === "error"
                  ? "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300"
                  : "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300"
            }`}
          >
            <span>{feedbackMessage.text}</span>
            <button
              onClick={() => setFeedbackMessage(null)}
              className="ml-auto p-0.5 hover:opacity-70"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Loading indicator */}
        {isSubmitting && (
          <div className="flex items-center gap-3 p-3 bg-plantista-50 dark:bg-plantista-800/50 rounded-xl">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-plantista-200 dark:border-plantista-700 border-t-plantista-500 dark:border-t-plantista-400" />
            <p className="text-sm text-plantista-600 dark:text-plantista-300">
              AI is editing your design... This may take 15-30 seconds.
            </p>
          </div>
        )}

        {/* Edit History — hidden in Pro mode (VersionTimeline handles it) */}
        {!useExternalCanvas && editHistory.length > 1 && (
          <div className="pt-2 border-t border-gray-100 dark:border-plantista-800">
            <p className="text-xs font-medium text-plantista-500 dark:text-plantista-400 mb-2">
              Edit History
            </p>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {editHistory.map((version, i) => (
                <button
                  key={i}
                  onClick={() => handleVersionSelect(version)}
                  className={`flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all ${
                    currentImageUrl === version.imageUrl
                      ? "border-plantista-500 dark:border-plantista-400 ring-2 ring-plantista-500/20 dark:ring-plantista-400/20"
                      : "border-gray-200 dark:border-plantista-700 hover:border-plantista-300 dark:hover:border-plantista-600"
                  }`}
                  title={version.prompt}
                >
                  <img
                    src={version.imageUrl}
                    alt={`Version ${i + 1}`}
                    className="w-16 h-12 object-cover"
                  />
                  <div className="px-1.5 py-0.5 text-[10px] font-medium text-center text-plantista-600 dark:text-plantista-400 bg-gray-50 dark:bg-plantista-800">
                    {i === 0 ? "Original" : `v${i + 1}`}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
